---
title: "Why a search returns no results"
description: "A candidate search in Chosen came back empty or missed someone you expected. The common causes — parsing gaps, over-narrow queries, wrong scope — and the fix for each."
category: "search"
keywords:
  - "search no results"
  - "candidate search not working"
  - "search missing candidates"
  - "empty search results"
  - "fix candidate search"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
  parent: "/guides/search"
---

A search that returns nothing — or skips someone you know is in your system — almost always has a plain cause. This page is the short list of them, with the fix for each. Work down it in order; the first two cover most cases.

## The resume never parsed

Search matches the text on a candidate's record. That text comes from [resume parsing](/guides/candidates/resume-parsing), which extracts structured fields and body text from each resume on upload.

When parsing has nothing to work with, search has nothing to match. The usual culprit is an **image-only PDF** — a resume that is a scan or an exported image rather than real text. Parsing can't read it, the extracted text is mostly empty, and every search mode will miss that candidate.

**Fix:** open the candidate and check whether their fields are populated. If the record is blank, the resume didn't parse. Re-upload a text-based version of the resume, or fill the key fields in by hand so search has something to match.

## The query is too narrow

A boolean query with several `AND` clauses stacked together is the most common way to get zero results. Each `AND` is another condition every candidate must satisfy at once.

```
"staff engineer" AND rust AND kubernetes AND fintech AND remote
```

That asks for one person who satisfies all five. You may not have them.

**Fix:** loosen it. Turn the must-haves into nice-to-haves with `OR`, or drop the weakest clause and add it back only if the result set is too big. Boolean is also literal — `kubernetes` will not match a resume that says "K8s". The [boolean syntax page](/guides/search/boolean-syntax) covers this.

## You're looking at the wrong scope

Search runs inside whatever you have open. If you are scoped to a single job, the search only covers that job's candidates — not everyone in your system. A candidate who applied to a different role won't appear.

**Fix:** check the scope. If you meant to search everyone, move out of the single-job view before running the query.

## You expected a saved search to be frozen

If you re-opened a saved search and the people changed, that is the design, not a bug. A saved search is a query, not a fixed list — re-running it scores your current pool, so results move as candidates come and go.

**Fix:** if you need an unchanging set of people, use a list instead. [Saved searches](/guides/search/saved-searches) explains the difference between the two.

## In deep search, the candidate missed the recall set

A deep search scores at most 150 candidates. Before scoring, the recall phase narrows your pool down to that cap. A candidate who exists in your system but didn't rank into the recall set is never scored — so they won't appear in the results, even though they match.

This happens on broad queries against a large pool. Recall keeps the strongest 150 and drops the rest.

**Fix:** narrow the query. Add a location, a seniority, a tool, a domain — anything that pushes the candidate you want higher in recall. [How a deep search run works](/guides/search/deep-search-runs) covers the cap in detail.

## Still nothing?

If the candidate's record looks complete, the query is loose, the scope is right, and they still don't surface, the catch-all is wording. Search responds to how you describe the role — try a plainer phrasing, or switch modes. Smart search reads intent; boolean matches exact text; [deep search](/guides/search/smart-vs-deep) reads the resume body. One of the three usually sees what the others missed.
